import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Star, Edit, Trash2, Award, MessageSquare, NotepadText, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import type { PublicAssessment, AssessmentScores } from '@arrowise/shared';
import { useAuth } from '@/providers/AuthProvider';

interface AssessmentListItemProps {
    assessment: PublicAssessment;
    currentUserId?: string;
    teamId?: string;
    onEdit?: (assessment: PublicAssessment) => void;
    onDelete?: (assessment: PublicAssessment) => void;
    isGraded?: boolean;
}

const categories = [
    { key: 'aiLiteracy' as keyof AssessmentScores, label: 'AI Literacy', icon: '🤖' },
    { key: 'codeOrganization' as keyof AssessmentScores, label: 'Code Organization', icon: '📁' },
    { key: 'problemUnderstanding' as keyof AssessmentScores, label: 'Problem Understanding', icon: '💡' },
    { key: 'solutionQuality' as keyof AssessmentScores, label: 'Solution Quality', icon: '✨' },
    { key: 'communication' as keyof AssessmentScores, label: 'Communication', icon: '💬' },
    { key: 'creativity' as keyof AssessmentScores, label: 'Creativity', icon: '🎨' },
    { key: 'efficiency' as keyof AssessmentScores, label: 'Efficiency', icon: '⚡' },
];

const AssessmentListItem: React.FC<AssessmentListItemProps> = ({
    assessment,
    currentUserId,
    teamId,
    onEdit,
    onDelete,
    isGraded = false
}) => {
    const { user, getUserTeamRole, isTeamOwner } = useAuth();
    const [isExpanded, setIsExpanded] = useState(false);
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const renderStars = (score: number | null) => {
        if (score === null) return <span className="text-muted-foreground text-sm italic">Not rated</span>;
        return (
            <div className="flex items-center gap-1">
                {Array.from({ length: 10 }, (_, i) => (
                    <Star
                        key={i}
                        className={`w-4 h-4 transition-colors duration-200 ${i < score ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'
                            }`}
                    />
                ))}
                <span className="text-sm font-semibold ml-2 text-foreground">{score}/10</span>
            </div>
        );
    };

    const calculateAverageScore = (scores: AssessmentScores) => {
        const validScores = Object.values(scores).filter(score => score !== null) as number[];
        if (validScores.length === 0) return null;
        return Math.round((validScores.reduce((sum, score) => sum + score, 0) / validScores.length) * 10) / 10;
    };

    const isComplete = () => {
        const scores = Object.values(assessment.scores);
        return scores.every(score => score !== null);
    };

    const isOwnAssessment = currentUserId && assessment.assessorId === currentUserId;

    // Permission checks
    const canEdit = isOwnAssessment;

    const canDelete = () => {
        if (!user || !teamId) {
            // Non-team member can only delete their own
            return isOwnAssessment;
        }

        const userRole = getUserTeamRole(teamId);
        const isOwner = isTeamOwner(teamId);

        if (isOwner || userRole === 'admin') {
            // Admin and owner can always delete
            return true;
        }

        // Member can only delete their own
        return isOwnAssessment;
    };

    const averageScore = calculateAverageScore(assessment.scores);

    return (
        <TooltipProvider>
            <Card className="w-full bg-card border-border shadow-lg hover:shadow-xl transition-all duration-300 ease-in-out overflow-hidden p-0">
                <CardHeader className="pt-6 bg-gradient-to-r from-primary/5 to-accent/5 border-b border-border rounded-t-lg">
                    <div className="flex items-center justify-between px-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                        <div className="flex items-center gap-4">
                            <div>
                                <CardTitle className="text-lg font-bold text-foreground flex items-center gap-2 mb-2">
                                    {assessment.assessorName}
                                    {isOwnAssessment && (
                                        <Badge variant="outline" className="bg-secondary text-secondary-foreground text-xs">
                                            You
                                        </Badge>
                                    )}
                                </CardTitle>
                                <div className="flex items-center gap-2 mt-1">
                                    {averageScore && (
                                        <Badge variant="secondary" className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-700/50 flex items-center gap-1 px-3 py-1">
                                            <Award className="w-3 h-3" />
                                            Avg: {averageScore}/10
                                        </Badge>
                                    )}
                                    {!isComplete() && (
                                        <Badge variant="outline" className="border-orange-300 text-orange-600 bg-orange-50 dark:border-orange-600 dark:text-orange-400 dark:bg-orange-900/20 px-3 py-1">
                                            Draft
                                        </Badge>
                                    )}
                                    {isComplete() && (
                                        <Badge variant="outline" className="border-green-300 text-green-600 bg-green-50 dark:border-green-600 dark:text-green-400 dark:bg-green-900/20 px-3 py-1">
                                            Complete
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {(canEdit || canDelete()) && (
                                <div className="flex items-center gap-1">
                                    {canEdit && onEdit && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!isGraded) {
                                                            onEdit(assessment);
                                                        }
                                                    }}
                                                    className={`h-9 w-9 p-0 bg-background hover:bg-primary/5 border border-border hover:border-primary/20 transition-all duration-200 ${isGraded ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <Edit className="w-4 h-4 text-primary" />
                                                </Button>
                                            </TooltipTrigger>
                                            {isGraded && (
                                                <TooltipContent sideOffset={8}>
                                                    <p>You can't edit an assessment when the candidate's session has been marked as assessed</p>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    )}
                                    {canDelete() && onDelete && (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (!isGraded) {
                                                            onDelete(assessment);
                                                        }
                                                    }}
                                                    className={`h-9 w-9 p-0 bg-background hover:bg-destructive/5 border border-border hover:border-destructive/20 transition-all duration-200 ${isGraded ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <Trash2 className="w-4 h-4 text-destructive" />
                                                </Button>
                                            </TooltipTrigger>
                                            {isGraded && (
                                                <TooltipContent sideOffset={8}>
                                                    <p>You can't delete an assessment when the candidate's session has been marked as assessed</p>
                                                </TooltipContent>
                                            )}
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-center pb-2 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                        <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                </CardHeader>

                {isExpanded && (
                    <CardContent className='pb-8'>
                        {/* Category Scores */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {categories.map((category) => (
                                <div key={category.key} className="bg-card rounded-xl p-4 border border-border shadow-sm hover:shadow-md transition-all duration-200">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{category.icon}</span>
                                            <span className="text-sm font-semibold text-foreground">{category.label}</span>
                                        </div>
                                        {renderStars(assessment.scores[category.key])}
                                    </div>
                                    <div className="rounded-lg p-3">
                                        <div className="flex items-start gap-2">
                                            <NotepadText className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                            {assessment.notes[category.key] ? (
                                                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{assessment.notes[category.key]}</p>
                                            ) : (
                                                <p className="text-sm text-muted-foreground/60 italic">No notes added</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Summary Note */}
                        <>
                            <Separator className="my-6 bg-gradient-to-r from-transparent via-border to-transparent" />
                            <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-xl p-5 border border-primary/10">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-primary-foreground">
                                        <MessageSquare className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                                            Summary note
                                        </h4>
                                        {assessment.overallNote ? (
                                            <p className="text-sm text-muted-foreground leading-relaxed bg-background/70 p-3 rounded-lg border border-border/50 whitespace-pre-wrap">
                                                {assessment.overallNote}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-muted-foreground/60 italic bg-background/70 p-3 rounded-lg border border-border/50">
                                                No overall notes added
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>

                        {/* Assessment Timeline */}
                        <div className="mt-6 pt-4 border-t border-border">
                            <div className="flex items-center justify-center gap-6 text-sm">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <div className="text-center">
                                        <div className="font-medium text-foreground">Created</div>
                                        <div className="text-xs">{formatDate(assessment.createdAt)}</div>
                                    </div>
                                </div>
                                {assessment.updatedAt !== assessment.createdAt && (
                                    <>
                                        <div className="w-px h-8 bg-border"></div>
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <div className="text-center">
                                                <div className="font-medium text-foreground">Updated</div>
                                                <div className="text-xs">{formatDate(assessment.updatedAt)}</div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </CardContent>
                )}
            </Card>
        </TooltipProvider>
    );
};

export default AssessmentListItem;
